import { useCallback, useEffect, useState } from 'react';
import { Receipt, ChevronDown, ChevronUp, Banknote, Smartphone, ShieldCheck, Info } from 'lucide-react';
import { patientApi, type PatientPaymentRow, type PatientPaymentDetail, type PaymentReceipt } from '../api';

/**
 * Invoices & payments — the server's own list (`GET /payments`).
 *
 * This page used to call `/lab/payments`, a route that does not exist. The
 * 404 was swallowed and every patient was told "No invoices yet", including
 * ones with unpaid bills. The real endpoint is the unified one, which is why
 * this page now shows every module's bills and says plainly which ones this
 * app cannot take money for.
 */

const STATUS_COLORS: Record<string, string> = {
  PAID: 'bg-green-100 text-green-700',
  PARTIAL: 'bg-yellow-100 text-yellow-700',
  PENDING: 'bg-red-100 text-red-700',
  UNPAID: 'bg-red-100 text-red-700',
};

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function fmtDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PatientLabInvoices() {
  const [rows, setRows] = useState<PatientPaymentRow[]>([]);
  const [dueTotal, setDueTotal] = useState(0);
  const [notPayable, setNotPayable] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, PatientPaymentDetail>>({});
  const [detailError, setDetailError] = useState<Record<string, string>>({});
  // A paid bill can show its receipt; an unpaid one can carry a transfer
  // reference to the provider. Neither is money moving through this app.
  const [receipts, setReceipts] = useState<Record<string, PaymentReceipt>>({});
  const [proofFor, setProofFor] = useState<string | null>(null);
  const [proofMethod, setProofMethod] = useState<'DIRECT_UPI' | 'BANK_TRANSFER'>('DIRECT_UPI');
  const [utr, setUtr] = useState('');
  const [proofNote, setProofNote] = useState('');
  const [busy, setBusy] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await patientApi.payments.list();
      setRows(d?.payments ?? []);
      setDueTotal(d?.dueTotal ?? 0);
      setNotPayable(d?.notPayableInApp ?? []);
    } catch (e: any) {
      setError(e?.body?.message || 'Your invoices could not be loaded. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const keyOf = (r: PatientPaymentRow) => `${r.module}:${r.reference}`;

  const showReceipt = async (r: PatientPaymentRow) => {
    const key = keyOf(r);
    setBusy(key); setActionMsg('');
    try {
      const rec = await patientApi.payments.receipt(r.module, r.reference);
      setReceipts((prev) => ({ ...prev, [key]: rec }));
    } catch (e: any) {
      setActionMsg(e?.body?.errors?.[0]?.message || e?.body?.message || 'No receipt is available for this bill yet.');
    } finally {
      setBusy('');
    }
  };

  const sendProof = async (r: PatientPaymentRow) => {
    const key = keyOf(r);
    if (utr.trim().length < 6) { setActionMsg('Enter the reference your payment app gave you.'); return; }
    setBusy(key); setActionMsg('');
    try {
      await patientApi.payments.submitProof(r.module, r.reference, { method: proofMethod, utr, note: proofNote });
      setProofFor(null); setUtr(''); setProofNote('');
      setActionMsg('Sent to the provider. The bill stays unpaid until they confirm it.');
      await load();
    } catch (e: any) {
      setActionMsg(e?.body?.errors?.[0]?.message || e?.body?.message || 'That reference could not be sent.');
    } finally {
      setBusy('');
    }
  };

  /** Open a row and pull its breakdown; the list itself carries only totals. */
  const toggle = async (r: PatientPaymentRow) => {
    const key = keyOf(r);
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (details[key] || detailError[key]) return;
    try {
      const d = await patientApi.payments.detail(r.module, r.reference);
      setDetails(prev => ({ ...prev, [key]: d }));
    } catch (e: any) {
      setDetailError(prev => ({ ...prev, [key]: e?.body?.message || 'This bill could not be opened.' }));
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Invoices &amp; Payments</h1>
        <p className="text-sm text-gray-500 mt-1">Bills raised to you across NeoHealthCard</p>
      </div>

      {!loading && !error && dueTotal > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-amber-800">
            <Banknote className="w-5 h-5 shrink-0" />
            <span className="text-sm font-semibold">Outstanding across your bills</span>
          </div>
          <span className="text-lg font-bold text-amber-900">{money(dueTotal)}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
          <p className="text-sm text-red-700 font-medium">{error}</p>
          <button
            onClick={() => void load()}
            className="mt-3 px-4 py-2 text-xs font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16">
          <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No invoices yet</p>
          <p className="text-sm text-gray-400 mt-1">Bills raised by a pharmacy or provider appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(inv => {
            const key = keyOf(inv);
            const isExpanded = expanded === key;
            const status = (inv.status || (inv.paid ? 'PAID' : 'PENDING')).toUpperCase();
            const detail = details[key];
            return (
              <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => void toggle(inv)}
                  className="w-full p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                        <Receipt className="w-5 h-5 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{inv.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[inv.providerName, inv.invoiceNumber, fmtDate(inv.paidAt || inv.createdAt)]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-bold text-gray-900">{money(inv.amount)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                        {status}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 p-5 space-y-4">
                    {detailError[key] ? (
                      <p className="text-xs text-red-600">{detailError[key]}</p>
                    ) : !detail ? (
                      <p className="text-xs text-gray-400">Loading the breakdown…</p>
                    ) : (
                      <>
                        {/* The provider's own line items */}
                        {detail.lines?.length > 0 && (
                          <div className="space-y-2">
                            {detail.lines.map((line, i) => (
                              <div key={`${line.label}-${i}`} className="flex items-center justify-between text-sm">
                                <span className="text-gray-700">{line.label}</span>
                                <span className="text-gray-900 font-medium">{money(line.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="border-t border-gray-100 pt-3 space-y-2 text-sm">
                          <div className="flex justify-between font-bold">
                            <span className="text-gray-900">Total</span>
                            <span className="text-teal-700">{money(detail.amount)}</span>
                          </div>
                          {detail.paid ? (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Paid</span>
                              <span className="text-green-700">
                                {[detail.paymentMethod, fmtDate(detail.paidAt)].filter(Boolean).join(' · ') || 'Recorded'}
                              </span>
                            </div>
                          ) : (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Due</span>
                              <span className="text-red-600 font-semibold">{money(detail.amount)}</span>
                            </div>
                          )}
                          {detail.receiptNumber && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Receipt</span>
                              <span className="font-mono text-gray-900">{detail.receiptNumber}</span>
                            </div>
                          )}
                        </div>

                        {/* How this provider actually takes money */}
                        {detail.methods && detail.methods.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
                            <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
                              <Banknote className="w-3.5 h-3.5" /> How to pay
                            </p>
                            {detail.methods.map(m => (
                              <div key={m.id} className="text-xs text-blue-900">
                                <span className="font-medium">{m.label}</span>
                                {m.note && <span className="text-blue-600"> — {m.note}</span>}
                              </div>
                            ))}
                            {detail.upi?.id && (
                              <div className="flex items-center justify-between text-xs pt-1 border-t border-blue-200">
                                <span className="text-blue-600 flex items-center gap-1"><Smartphone className="w-3 h-3" />UPI</span>
                                <span className="font-mono font-medium text-blue-900">{detail.upi.id}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {detail.insurance?.note && (
                          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-[11px] text-gray-600 flex gap-2">
                            <ShieldCheck className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                            <span>{detail.insurance.note}</span>
                          </div>
                        )}

                        {detail.gateway && !detail.gateway.available && detail.gateway.note && (
                          <div className="text-[11px] text-gray-500 flex gap-2">
                            <Info className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                            <span>{detail.gateway.note}</span>
                          </div>
                        )}

                        {/* A transfer already sent and waiting on the provider */}
                        {detail.pendingProof?.utr && (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-800">
                            Reference <span className="font-mono">{detail.pendingProof.utr}</span> is with {detail.providerName ?? 'the provider'} to confirm.
                            The bill stays unpaid until they do.
                          </div>
                        )}

                        {/* Paid → its receipt. Unpaid → tell the provider about a transfer. */}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {detail.paid ? (
                            <button
                              onClick={() => void showReceipt(inv)}
                              disabled={busy === key}
                              className="px-3 py-2 text-xs font-semibold text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 disabled:opacity-50"
                            >
                              {busy === key ? 'Fetching…' : receipts[key] ? 'Receipt shown below' : 'View receipt'}
                            </button>
                          ) : (
                            !detail.pendingProof?.utr && (
                              <button
                                onClick={() => { setProofFor(proofFor === key ? null : key); setActionMsg(''); }}
                                className="px-3 py-2 text-xs font-semibold text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50"
                              >
                                {proofFor === key ? 'Close' : 'I paid by transfer'}
                              </button>
                            )
                          )}
                        </div>

                        {actionMsg && (expanded === key) && (
                          <p className="text-[11px] text-gray-600">{actionMsg}</p>
                        )}

                        {/* The reference goes to the provider; this app takes no money. */}
                        {proofFor === key && !detail.paid && (
                          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
                            <p className="text-[11px] text-gray-600">
                              Already paid {detail.providerName ?? 'them'} directly? Send the reference so they can match it.
                              NeoHealthCard does not collect or confirm the money.
                            </p>
                            <div className="flex gap-2">
                              {(['DIRECT_UPI', 'BANK_TRANSFER'] as const).map(m => (
                                <button
                                  key={m}
                                  onClick={() => setProofMethod(m)}
                                  className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg border ${proofMethod === m ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}
                                >
                                  {m === 'DIRECT_UPI' ? 'UPI' : 'Bank transfer'}
                                </button>
                              ))}
                            </div>
                            <input
                              aria-label="Payment reference"
                              value={utr}
                              onChange={(e) => setUtr(e.target.value)}
                              placeholder="UTR / reference from your payment app"
                              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg font-mono"
                            />
                            <input
                              aria-label="Note for the provider"
                              value={proofNote}
                              onChange={(e) => setProofNote(e.target.value)}
                              placeholder="Anything they should know (optional)"
                              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg"
                            />
                            <button
                              onClick={() => void sendProof(inv)}
                              disabled={busy === key}
                              className="px-4 py-2 text-xs font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                            >
                              {busy === key ? 'Sending…' : 'Send the reference'}
                            </button>
                          </div>
                        )}

                        {/* The receipt as the provider recorded it */}
                        {receipts[key] && (
                          <div className="bg-white border border-teal-200 rounded-xl p-4 space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-bold text-gray-900">Receipt {receipts[key].receiptNumber ?? ''}</p>
                              <span className="text-[10px] text-gray-500">{fmtDate(receipts[key].paidAt)}</span>
                            </div>
                            <p className="text-[11px] text-gray-600">
                              {[receipts[key].providerName, receipts[key].invoiceNumber, receipts[key].paymentMethod].filter(Boolean).join(' · ')}
                            </p>
                            {receipts[key].lines?.map((l, i) => (
                              <div key={`${l.label}-${i}`} className="flex justify-between text-[11px]">
                                <span className="text-gray-600">{l.label}</span>
                                <span className="text-gray-900">{money(l.amount)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-xs font-bold pt-1 border-t border-gray-100">
                              <span>Paid</span>
                              <span className="text-teal-700">{money(receipts[key].amount)}</span>
                            </div>
                            <p className="text-[10px] text-gray-400">
                              Issued by {receipts[key].providerName ?? 'the provider'} for {receipts[key].patientNhcId ?? 'you'}.
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* The server's own list of what it cannot collect for. */}
      {!loading && !error && notPayable.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
          <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 leading-relaxed">
            <p className="font-semibold mb-1">Settled directly with the provider</p>
            <p>{notPayable.join(', ')}. NeoHealthCard does not process these payments and never marks them paid on its own.</p>
          </div>
        </div>
      )}
    </div>
  );
}
